import React, { useState } from 'react';
import { useQuery, useQueries, useMutation, useQueryClient } from '@tanstack/react-query';
import { PlusIcon, TrashIcon, ToggleLeftIcon, ToggleRightIcon, KeyIcon, ChevronRightIcon, ChevronDownIcon } from 'lucide-react';
import { apiClient, TenantResponse, ApiKeyResponse } from '../lib/api';
import CreateApiKeyModal from '../components/CreateApiKeyModal';
import ConfirmationModal from '../components/ConfirmationModal';

export default function ApiKeys() {
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [expandedTenants, setExpandedTenants] = useState<Set<string>>(new Set());
  const [selectedTenant, setSelectedTenant] = useState<TenantResponse | null>(null);
  const [selectedApiKey, setSelectedApiKey] = useState<ApiKeyResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  // Fetch tenants
  const { data: tenants = [], isLoading: isLoadingTenants, error: fetchTenantsError } = useQuery<TenantResponse[], Error>({
    queryKey: ['tenants'],
    queryFn: apiClient.getTenants,
    retry: 3,
    retryDelay: 1000,
  });

  // Fetch API keys for all tenants using useQueries
  const apiKeyQueries = useQueries({
    queries: tenants.map(tenant => ({
      queryKey: ['apiKeys', tenant.id],
      queryFn: () => apiClient.getApiKeys(tenant.id),
      retry: 3,
      retryDelay: 1000,
      staleTime: 5 * 60 * 1000, // 5 minutes
    })),
  });

  // Helper function to get API keys for a specific tenant
  const getApiKeysForTenant = (tenantId: string): ApiKeyResponse[] => {
    const tenantIndex = tenants.findIndex(t => t.id === tenantId);
    if (tenantIndex === -1) return [];
    const query = apiKeyQueries[tenantIndex];
    return query?.data || [];
  };

  // Helper function to check if API keys are loading for a specific tenant
  const isLoadingApiKeysForTenant = (tenantId: string): boolean => {
    const tenantIndex = tenants.findIndex(t => t.id === tenantId);
    if (tenantIndex === -1) return false;
    const query = apiKeyQueries[tenantIndex];
    return query?.isLoading || false;
  };

  // Helper function to get error for a specific tenant
  const getApiKeysErrorForTenant = (tenantId: string): Error | null => {
    const tenantIndex = tenants.findIndex(t => t.id === tenantId);
    if (tenantIndex === -1) return null;
    const query = apiKeyQueries[tenantIndex];
    return (query?.error as Error) || null;
  };

  // Helper function to toggle tenant expansion
  const toggleTenantExpansion = (tenantId: string) => {
    const newExpanded = new Set(expandedTenants);
    if (newExpanded.has(tenantId)) {
      newExpanded.delete(tenantId);
    } else {
      newExpanded.add(tenantId);
    }
    setExpandedTenants(newExpanded);
  };

  // Mutation for deleting API key
  const deleteApiKeyMutation = useMutation({
    mutationFn: (data: { apiKeyId: string; tenantId: string }) => apiClient.deleteApiKey(data.apiKeyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys', variables.tenantId] });
      setError('');
    },
    onError: (err: unknown) => {
      setError(String((err as Error).message || err) || 'Failed to delete API key.');
    },
  });

  // Mutation for toggling API key status
  const toggleApiKeyStatusMutation = useMutation({
    mutationFn: (data: { apiKeyId: string; tenantId: string }) => apiClient.toggleApiKeyStatus(data.apiKeyId),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['apiKeys', variables.tenantId] });
      setError('');
    },
    onError: (err: unknown) => {
      setError(String((err as Error).message || err) || 'Failed to toggle API key status.');
    },
  });

  const handleCreateApiKey = (tenant: TenantResponse) => {
    setSelectedTenant(tenant);
    setCreateModalOpen(true);
  };

  const handleDeleteApiKey = (apiKey: ApiKeyResponse) => {
    setSelectedApiKey(apiKey);
    setDeleteModalOpen(true);
  };

  const confirmDeleteApiKey = () => {
    if (selectedApiKey && selectedTenant) {
      deleteApiKeyMutation.mutate({ 
        apiKeyId: selectedApiKey.id,
        tenantId: selectedTenant.id 
      });
      setDeleteModalOpen(false);
      setSelectedApiKey(null);
    }
  };

  const handleToggleApiKeyStatus = (apiKey: ApiKeyResponse, tenantId: string) => {
    toggleApiKeyStatusMutation.mutate({ 
      apiKeyId: apiKey.id,
      tenantId: tenantId 
    });
  };

  const closeCreateModal = () => {
    setCreateModalOpen(false);
    setSelectedTenant(null);
  };

  const closeDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedApiKey(null);
  };

  const maskApiKey = (keyId: string) => {
    if (!keyId) return '';
    return `sk_...${keyId.slice(-4)}`;
  };

  if (isLoadingTenants) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-lg text-gray-600">Loading tenants...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="sm:flex sm:items-center">
        <div className="sm:flex-auto">
          <h1 className="text-2xl font-bold leading-7 text-gray-900 sm:truncate sm:text-3xl sm:tracking-tight">
            API Keys
          </h1>
          <p className="mt-2 text-sm text-gray-700">
            Manage API keys for your tenants. Super admins can generate and manage keys for any tenant.
          </p>
        </div>
      </div>

      {(error || fetchTenantsError) && (
        <div className="mt-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
          {
            [error, fetchTenantsError]
              .map(err => (err instanceof Error ? err.message : String(err || '')))
              .filter(Boolean)
              .join('; ') || 'An unexpected error occurred.'
          }
        </div>
      )}

      <div className="mt-8 flex flex-col">
        <div className="-my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-gray-300">
                <thead className="bg-gray-50">
                  <tr>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Tenant Name
                    </th>
                    <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      API Keys
                    </th>
                    <th scope="col" className="relative px-6 py-3">
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {tenants.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                        No tenants found.
                      </td>
                    </tr>
                  ) : (
                    tenants.map((tenant) => {
                      const isExpanded = expandedTenants.has(tenant.id);
                      const apiKeys = getApiKeysForTenant(tenant.id);
                      const isLoading = isLoadingApiKeysForTenant(tenant.id);
                      const apiKeyError = getApiKeysErrorForTenant(tenant.id);
                      
                      return (
                        <React.Fragment key={tenant.id}>
                          <tr className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                              <button
                                onClick={() => toggleTenantExpansion(tenant.id)}
                                className="flex items-center w-full text-left focus:outline-none rounded-md"
                              >
                                {isExpanded ? (
                                  <ChevronDownIcon className="h-5 w-5 text-gray-400 mr-2" />
                                ) : (
                                  <ChevronRightIcon className="h-5 w-5 text-gray-400 mr-2" />
                                )}
                                {tenant.name}
                              </button>
                            </td>
                            <td className="px-6 py-4 text-sm text-gray-500">
                              <div className="flex items-center">
                                {isLoading ? (
                                  <span className="text-xs text-gray-400">Loading...</span>
                                ) : apiKeyError ? (
                                  <span className="text-xs text-red-500">Error loading keys</span>
                                ) : (
                                  <span className="text-xs text-gray-600">
                                    {apiKeys.length} key{apiKeys.length !== 1 ? 's' : ''}
                                    {apiKeys.length > 0 && (
                                      <span className="ml-2">
                                        ({apiKeys.filter(key => key.is_active).length} active)
                                      </span>
                                    )}
                                  </span>
                                )}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                              <button
                                type="button"
                                onClick={() => handleCreateApiKey(tenant)}
                                className="inline-flex items-center justify-center rounded-md border border-transparent bg-primary-600 px-3 py-2 text-sm font-medium text-white shadow-sm hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2"
                              >
                                <PlusIcon className="-ml-1 mr-1 h-4 w-4" />
                                Generate Key
                              </button>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr>
                              <td colSpan={3} className="px-6 py-4 bg-gray-50">
                                <div className="space-y-3">
                                  {isLoading ? (
                                    <div className="text-center py-4">
                                      <span className="text-sm text-gray-500">Loading API keys...</span>
                                    </div>
                                  ) : apiKeyError ? (
                                    <div className="text-center py-4">
                                      <span className="text-sm text-red-600">Failed to load API keys: {apiKeyError.message}</span>
                                    </div>
                                  ) : apiKeys.length === 0 ? (
                                    <div className="text-center py-4">
                                      <span className="text-sm text-gray-500">No API keys generated for this tenant.</span>
                                    </div>
                                  ) : (
                                    apiKeys.map((key) => {
                                      // Store tenant info for delete modal
                                      const handleDelete = () => {
                                        setSelectedTenant(tenant);
                                        handleDeleteApiKey(key);
                                      };
                                      
                                      return (
                                        <div key={key.id} className="flex items-center justify-between bg-white p-4 rounded-lg border border-gray-200">
                                          <div className="flex items-center">
                                            <KeyIcon className="h-5 w-5 text-gray-500 mr-3" />
                                            <div className="flex flex-col">
                                              <div className="flex items-center">
                                                <span className="font-mono text-sm text-gray-800 mr-3">{maskApiKey(key.id)}</span>
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                                  key.is_active 
                                                    ? 'bg-green-100 text-green-800' 
                                                    : 'bg-red-100 text-red-800'
                                                }`}>
                                                  {key.is_active ? 'Active' : 'Inactive'}
                                                </span>
                                              </div>
                                              {key.name && 
                                                <span className="text-xs text-gray-600 mt-1">Name: {key.name}</span>
                                              }
                                              {key.description && 
                                                <span className="text-xs text-gray-600 mt-1">Description: {key.description}</span>
                                              }
                                              {key.allowed_origins && key.allowed_origins.length > 0 &&
                                                <span className="text-xs text-gray-600 mt-1">Allowed Origins: {key.allowed_origins.join(', ')}</span>
                                              }
                                              <span className="text-xs text-gray-600 mt-1">Created: {new Date(key.created_at).toLocaleDateString()}</span>
                                              {key.last_used_at && 
                                                <span className="text-xs text-gray-600 mt-1">Last Used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                                              }
                                              {key.expires_at && 
                                                <span className="text-xs text-gray-600 mt-1">Expires: {new Date(key.expires_at).toLocaleDateString()}</span>
                                              }
                                            </div>
                                          </div>
                                          <div className="flex items-center space-x-2">
                                            <button
                                              onClick={() => handleToggleApiKeyStatus(key, tenant.id)}
                                              disabled={toggleApiKeyStatusMutation.isLoading}
                                              className="text-primary-600 hover:text-primary-900 disabled:opacity-50"
                                              title={key.is_active ? "Deactivate Key" : "Activate Key"}
                                            >
                                              {key.is_active ? (
                                                <ToggleRightIcon className="h-6 w-6" />
                                              ) : (
                                                <ToggleLeftIcon className="h-6 w-6" />
                                              )}
                                            </button>
                                            <button 
                                              onClick={handleDelete}
                                              disabled={deleteApiKeyMutation.isLoading}
                                              className="text-red-600 hover:text-red-900 disabled:opacity-50"
                                              title="Delete Key"
                                            >
                                              <TrashIcon className="h-6 w-6" />
                                            </button>
                                          </div>
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Create API Key Modal */}
      <CreateApiKeyModal
        isOpen={createModalOpen}
        onClose={closeCreateModal}
        tenant={selectedTenant}
      />

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        isOpen={deleteModalOpen}
        onClose={closeDeleteModal}
        onConfirm={confirmDeleteApiKey}
        title="Delete API Key"
        message={`Are you sure you want to delete the API key "${selectedApiKey?.name || maskApiKey(selectedApiKey?.id || '')}"? This action cannot be undone.`}
        confirmText="Delete Key"
        type="danger"
        isLoading={deleteApiKeyMutation.isLoading}
      />
    </div>
  );
}
